/**
 * Which payment requests the reminder letters chase, and when.
 *
 * The letters themselves are in `paymentEmails.ts`; this is the rule the
 * every-other-day sweep applies before it writes one. Kept apart so that
 * each file is about one thing: what a customer reads, and whether they are
 * written to at all.
 */

import { PAYMENT_SPLIT_THRESHOLD } from "./paymentSchedule.js";

/** How often a reminder goes out. Jonathon, 2026-09-06: "every other day". */
export const REMINDER_EVERY_DAYS = 2;

/**
 * How long the reminders wait for a check a rep has said is coming.
 *
 * A check in the mail takes a week or so to arrive and be recorded; two
 * weeks is long enough that an honest one has turned up, and short enough
 * that a promised one that never comes is chased again before anybody has
 * forgotten it. Jonathon agreed the fortnight on 2026-10-04 (ADR 0263).
 */
export const CHECK_IN_THE_MAIL_DAYS = 14;

/**
 * Whether a check a rep said was coming is still being waited for.
 *
 * True from the moment they said so until `CHECK_IN_THE_MAIL_DAYS` later; a
 * date that cannot be read is no promise at all. Recording the check clears
 * the promise outright (the database does that), so this is only ever asked
 * about a check that has not been recorded.
 */
export function checkStillExpected(
  expectedAt: string | Date | null | undefined,
  now: Date,
): boolean {
  const since = expectedAt == null ? null : checkExpectedUntil(expectedAt);
  if (since == null) return false;
  return now.getTime() < since.getTime();
}

/**
 * The day the reminders start again if the check has not arrived: when the
 * rep said it was coming plus `CHECK_IN_THE_MAIL_DAYS`. Null when the date
 * cannot be read.
 */
export function checkExpectedUntil(
  expectedAt: string | Date | null | undefined,
): Date | null {
  if (expectedAt == null) return null;
  const at = expectedAt instanceof Date ? expectedAt : new Date(expectedAt);
  if (Number.isNaN(at.getTime())) return null;
  return new Date(at.getTime() + CHECK_IN_THE_MAIL_DAYS * 86_400_000);
}

/**
 * Whether a request is due another reminder.
 *
 * Every other day from the last letter, whichever letter that was — the first
 * one counts, so nobody gets two emails on the day they sign. A request that
 * has never had a letter is due one, which is what makes this safe to call on a
 * row created before this feature existed.
 */
export function dueForReminder(
  lastLetterAt: string | Date | null | undefined,
  now: Date,
): boolean {
  if (lastLetterAt == null) return true;
  const last =
    lastLetterAt instanceof Date ? lastLetterAt : new Date(lastLetterAt);
  if (Number.isNaN(last.getTime())) return true;
  const days = (now.getTime() - last.getTime()) / 86_400_000;
  return days >= REMINDER_EVERY_DAYS;
}

/**
 * Whether a request is one the letters should chase at all.
 *
 * A customer who chose financing is applying to a lender, not ignoring an
 * invoice, and there is no invoice — no pay link — to put in the letter. Until
 * 2026-09-06 the reminders looked only at the state and the amount, and would
 * have written to them every other day regardless. The request itself stays
 * open (`mercury_payment_requests` is untouched by this): the money is still
 * owed, and a rep closes it when the lender pays or calls it off.
 *
 * Nor is a customer whose payment is clearing. Mercury reports an invoice as
 * Processing from the moment they pay until the money lands, one to five
 * business days for an ACH debit, and the poll stamps `payment_started_at`
 * the first time it sees that.
 *
 * **The balance only once a person has sent it.** Jonathon, 2026-09-10,
 * after job 1021: "make sure you dont send that automated email at this time.
 * i'm fine with a prompt popping up for a user to send but he needs the option
 * to cancel the email." The rep closed the prompt without sending; the request
 * was still there with no letter against it, `dueForReminder` read that as
 * overdue, and the next sweep wrote to the customer on its own. So the balance
 * is never the first letter a reminder sends. Once a rep HAS pressed Send
 * (`first_email_at`), it is reminded like any other — Jonathon, 2026-10-03:
 * "it should send reminders on the final payment once that email is sent
 * until the customer initiates it".
 *
 * **Not the first payment on a job under $1,000.** There is no first payment
 * there: the schedule takes the whole contract at completion, and the signing
 * page offers the cards without asking. Job 1068 (Jim Messer, $840) was
 * reminded about a "first payment" three times, the last after he had paid
 * the whole job on the final link (2026-10-03).
 *
 * **Nothing on a job whose final payment has started.** Jonathon, 2026-10-03:
 * once the customer has initiated the final, every reminder on the job stops —
 * the final is the rest of the job, and an older request still open beside it
 * is ours to tidy, not theirs to be asked about. Nor on a job whose customer
 * chose financing on any payment: asked about the final-payment reminder, "don't
 * send it [to] customers that financ[ed]" (Jonathon, 2026-10-03).
 *
 * **Nor while a rep has said a check is coming.** A customer who has told the
 * rep they put a check in the mail, and is then reminded every other day
 * until it is recorded, rings to ask whether it was lost (2026-10-04). The
 * rep says so on the job, and every reminder on the job waits
 * `CHECK_IN_THE_MAIL_DAYS`; recording the check ends it, and so does the
 * fortnight running out with nothing recorded (ADR 0263).
 *
 * **Nor on a job whose reminders a person has turned off.** Jonathon,
 * 2026-10-24: "is there a turn off payment reminder button per job for a
 * customer. if not there needs to be one". Off until somebody turns them back
 * on; the payment requests and their links stay as they are (ADR 0302).
 */
export function worthReminding(row: ReminderRow): boolean {
  if (!stillAwaited(row)) return false;
  // The job, not just this request: nothing once the final has started, and
  // nothing for a customer who chose financing on any payment.
  if (row.balance_started || row.financed) return false;
  if (isBalance(row)) {
    return row.first_email_at != null && row.first_email_at !== "";
  }
  return !(isFirst(row) && underSplit(row.contract_cents));
}

export interface ReminderRow {
  state: string | null | undefined;
  amount_cents: number | string | null | undefined;
  customer_method: string | null | undefined;
  /** Stamped by the poll when Mercury first says the invoice is Processing. */
  payment_started_at?: string | Date | null;
  /** 'final' is the balance, which only a person sends. */
  letter_kind?: string | null;
  /** 1 is the first payment, 4 the balance. */
  slot?: number | string | null;
  /** The contract this request was priced against. */
  contract_cents?: number | string | null;
  /** When a letter about this request first actually went. */
  first_email_at?: string | Date | null;
  /** Whether this job's final payment has been started or paid. */
  balance_started?: boolean | null;
  /** Whether this job's customer chose financing on any payment. */
  financed?: boolean | null;
  /**
   * Whether a rep has said a check is coming for this job and the fortnight
   * has not run out: `checkStillExpected(jobs.check_expected_at, now)`.
   */
  awaiting_check?: boolean | null;
  /** Whether a person has turned the payment reminders off on this job. */
  reminders_off?: boolean | null;
}

/** Open, money on it, not yet paying, not with a lender, no check promised. */
function stillAwaited(row: ReminderRow): boolean {
  if (row.state !== "pending" && row.state !== "sent") return false;
  // The rep has been told a check is in the post. Chasing it reads as not
  // having listened.
  if (row.awaiting_check) return false;
  // A person has turned the reminders off on this job (ADR 0302).
  if (row.reminders_off) return false;
  // They have paid; the bank is taking its days. A reminder now reads as
  // "we did not notice", to a customer who did exactly what was asked.
  if (row.payment_started_at) return false;
  const amount = row.amount_cents == null ? NaN : Number(row.amount_cents);
  if (!Number.isFinite(amount) || amount <= 0) return false;
  return row.customer_method !== "financing";
}

function isBalance(row: ReminderRow): boolean {
  return row.letter_kind === "final" || Number(row.slot) === 4;
}

function isFirst(row: ReminderRow): boolean {
  return row.letter_kind === "first" || Number(row.slot) === 1;
}

/**
 * A job under $1,000, which has no first payment. Unknown is not "small": a
 * row that cannot say what it was priced against is chased as it always was.
 */
function underSplit(
  contractCents: number | string | null | undefined,
): boolean {
  const contract = contractCents == null ? NaN : Number(contractCents);
  return Number.isFinite(contract) && contract < PAYMENT_SPLIT_THRESHOLD;
}

/**
 * How many reminders one sweep writes at most. The cap is on letters, not on
 * the rows read to find them: until 2026-10-05 the sweep read fifty open
 * requests and only then dropped the ones it never chases, so once fifty of
 * those had built up (an unsent balance, a first payment on a small job,
 * anything on a financed job) a customer who was owed a reminder got none,
 * and the run still reported nothing wrong (issue #690).
 */
export const REMINDER_BATCH = 50;

/** What the job says, which no single request on it does. */
export interface ReminderJobFacts {
  /** Jobs whose final payment has been started or paid. */
  balanceStarted: Set<string>;
  /** Jobs whose customer chose financing on any payment. */
  financed: Set<string>;
  /** Jobs where a rep has said a check is coming and the fortnight is not up. */
  awaitingCheck: Set<string>;
  /** Jobs whose payment reminders a person has turned off (ADR 0302). */
  remindersOff: Set<string>;
}

/** A request on the job, open or not, as much as `reminderJobFacts` reads. */
export interface ReminderJobRequest {
  job_id: string;
  slot: number | string | null;
  state: string | null;
  payment_started_at: string | Date | null;
  customer_method: string | null;
}

/**
 * What the job itself says about its reminders: the promised check,
 * `jobs.check_expected_at`, and whether a person has turned them off,
 * `jobs.payment_reminders_off_at`.
 */
export interface CheckOnTheJob {
  id: string;
  check_expected_at: string | Date | null;
  payment_reminders_off_at?: string | Date | null;
}

/**
 * Reads the job-level facts `worthReminding` needs off every request on the
 * jobs (not just the open ones: the request that settles it is usually
 * closed) and the checks reps have said are coming.
 */
export function reminderJobFacts(
  requests: readonly ReminderJobRequest[],
  checks: readonly CheckOnTheJob[],
  now: Date,
): ReminderJobFacts {
  const facts: ReminderJobFacts = {
    balanceStarted: new Set(),
    financed: new Set(),
    awaitingCheck: new Set(),
    remindersOff: new Set(),
  };
  for (const row of requests) {
    if (
      Number(row.slot) === 4 &&
      (row.state === "paid" || row.payment_started_at)
    ) {
      facts.balanceStarted.add(row.job_id);
    }
    if (row.customer_method === "financing") facts.financed.add(row.job_id);
  }
  for (const job of checks) {
    if (checkStillExpected(job.check_expected_at, now)) {
      facts.awaitingCheck.add(job.id);
    }
    if (job.payment_reminders_off_at) facts.remindersOff.add(job.id);
  }
  return facts;
}

/** An open request the sweep might write about. */
export interface ReminderCandidate extends ReminderRow {
  job_id: string;
  last_letter_at?: string | Date | null;
}

/**
 * The requests one sweep writes a reminder about, in the order given: those
 * worth chasing and due a letter, and at most `limit` of them. The cap is
 * applied after the filter, so rows that are never chased cannot use up the
 * batch.
 */
export function remindersToSend<T extends ReminderCandidate>(
  rows: readonly T[],
  facts: ReminderJobFacts,
  now: Date,
  limit: number = REMINDER_BATCH,
): T[] {
  const chosen: T[] = [];
  for (const row of rows) {
    if (chosen.length >= limit) break;
    if (
      !worthReminding({
        ...row,
        balance_started: facts.balanceStarted.has(row.job_id),
        financed: facts.financed.has(row.job_id),
        awaiting_check: facts.awaitingCheck.has(row.job_id),
        reminders_off: facts.remindersOff.has(row.job_id),
      })
    ) {
      continue;
    }
    if (!dueForReminder(row.last_letter_at, now)) continue;
    chosen.push(row);
  }
  return chosen;
}
